// Поставщики («Закуп») и Оптовики в кабинете руководителя — только просмотр.

export type PartnerKind = 'supplier' | 'wholesaler';

export interface PartnerRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  city: string | null;
  logoUrl: string | null;
  /** Модерация: pending — ждёт одобрения, active — работает, blocked — скрыт оператором. */
  status: 'pending' | 'active' | 'blocked';
  subscriptionStatus: 'trial' | 'active' | 'past_due' | 'cancelled';
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  accessEndsAt: string | null;
  daysLeft: number | null;
  accessState: 'ok' | 'blocked';
  autoRenew: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  // Поставщик
  inn?: string | null;
  legalName?: string | null;
  productsCount?: number;
  // Оптовик
  itemsCount?: number;
  salesCount?: number;
}

export interface PartnerPayment {
  id: string;
  amountKopecks: number;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  method: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface PartnerCardData {
  kind: PartnerKind;
  id: string;
  name: string;
  email: string;
  phone: string | null;
  logoUrl: string | null;
  city: string | null;
  status: PartnerRow['status'];
  subscriptionStatus: PartnerRow['subscriptionStatus'];
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  accessEndsAt: string | null;
  daysLeft: number | null;
  accessState: 'ok' | 'blocked';
  autoRenew: boolean;
  priceKopecks: number;
  createdAt: string;
  lastLoginAt: string | null;
  payments: PartnerPayment[];
  // Поставщик
  description?: string | null;
  legalForm?: string | null;
  legalName?: string | null;
  inn?: string | null;
  contactPerson?: string | null;
  warehouseAddress?: string | null;
  deliveryRegions?: string[];
  counts: { products?: number; orders?: number; items?: number; sales?: number; couriers?: number };
}

export const KIND: Record<
  PartnerKind,
  { path: string; title: string; one: string; empty: string; back: string }
> = {
  supplier: {
    path: '/suppliers',
    title: 'Поставщики',
    one: 'Поставщик',
    empty: 'Поставщиков пока нет.',
    back: 'Поставщики',
  },
  wholesaler: {
    path: '/wholesalers',
    title: 'Оптовики',
    one: 'Оптовик',
    empty: 'Оптовиков пока нет.',
    back: 'Оптовики',
  },
};

export const MODERATION_LABEL: Record<PartnerRow['status'], string> = {
  pending: 'На модерации',
  active: 'Одобрен',
  blocked: 'Заблокирован',
};

const SUB_LABEL: Record<PartnerRow['subscriptionStatus'], string> = {
  trial: 'Триал',
  active: 'Активна',
  past_due: 'Просрочена',
  cancelled: 'Отменена',
};

/** Плашка подписки по фактическому доступу, как у заведений: срок вышел — «Просрочена». */
export function partnerSubLabel(p: Pick<PartnerRow, 'subscriptionStatus' | 'accessState'>): string {
  if (p.accessState === 'blocked' && p.subscriptionStatus !== 'cancelled') return 'Просрочена';
  return SUB_LABEL[p.subscriptionStatus] ?? p.subscriptionStatus;
}

export function partnerSubTone(
  p: Pick<PartnerRow, 'subscriptionStatus' | 'accessState' | 'daysLeft'>,
): 'green' | 'amber' | 'red' | 'slate' {
  if (p.accessState === 'blocked') return p.subscriptionStatus === 'cancelled' ? 'slate' : 'red';
  if (p.subscriptionStatus === 'trial') return 'amber';
  return p.daysLeft != null && p.daysLeft <= 3 ? 'amber' : 'green';
}

/** Горит: доступ закрыт или кончается в ближайшие 3 дня — кому звонить первым. */
export function partnerExpiring(p: Pick<PartnerRow, 'accessState' | 'daysLeft'>): boolean {
  return p.accessState === 'blocked' || (p.daysLeft ?? 99) <= 3;
}

export const PAYMENT_STATUS_LABEL: Record<PartnerPayment['status'], string> = {
  pending: 'ожидает',
  succeeded: 'оплачено',
  failed: 'не прошла',
  refunded: 'возврат',
};

export const LEGAL_FORM_LABEL: Record<string, string> = {
  OOO: 'ООО',
  IP: 'ИП',
  SAMOZANYATY: 'Самозанятый',
  AO: 'АО',
};
